import { useEffect, useRef, useState } from "react";
import { Download, ChevronDown } from "lucide-react";
import { exportDocument, EXPORT_FORMATS } from "@/core/docExport";

/**
 * Small dropdown to export a document as Markdown / Word / plain text.
 * `getMarkdown` is called at click time so the latest content is exported.
 */
export default function ExportMenu({ baseName, getMarkdown }: { baseName: string; getMarkdown: () => string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted transition-colors hover:text-text"
      >
        <Download className="h-4 w-4" /> Export <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-lg border border-border bg-surface shadow-2xl">
          {EXPORT_FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                exportDocument(baseName, getMarkdown(), f.id);
                setOpen(false);
              }}
              className="block w-full px-3 py-2 text-left text-sm text-text transition-colors hover:bg-surface-2"
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
